import { useQuery } from "@tanstack/react-query";
import type React from "react";
import { Link } from "react-router";

import type { CommunityFeedAnalyticsDto, MessageDetailV2Dto } from "@/api/types";
import { MessageSquare02Icon } from "@/components/Icons/MessageSquare02Icon";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import { formatDistance } from "@/helpers/date";
import { isDefined } from "@/helpers/util";
import { useSlug } from "@/hooks/useSlug";
import {
  getReactionImage,
  getReactionLabel,
  getTotalReactionAmount,
} from "@/modules/messages/pages/List/components/CommunityPost/helpers";
import { useAnalyticsQueries } from "@/queries/analytics";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import { AnalyticsWidgetCard } from "../AnalyticsWidgetCard";

const FALLBACK_DATA: CommunityFeedAnalyticsDto = {
  lastUpdatedAt: new Date().toISOString(),
  totalResidentPostCount: 0,
};
export function CommunityFeedInsightsWidget(): React.ReactNode {
  const slug = useSlug();
  const { t } = useTranslation();
  const analyticsQueries = useAnalyticsQueries();
  const { data: communityFeedStats, isPending, isError, error } = useQuery(analyticsQueries.getCommunityFeedStats());

  // If BE returns all data as null, this implies that the project does not yet have an active community feed.
  // Therefore we hide the widget
  if (
    !isError &&
    !isPending &&
    !isDefined(communityFeedStats?.totalResidentPostCount) &&
    !communityFeedStats?.mostReactedMessage &&
    !communityFeedStats?.mostCommentedMessage
  ) {
    return null;
  }

  const isDataAvailable =
    !!communityFeedStats &&
    Boolean(communityFeedStats.totalResidentPostCount) &&
    Boolean(communityFeedStats.mostReactedMessage) &&
    Boolean(communityFeedStats.mostCommentedMessage);
  const primaryData = isDataAvailable ? communityFeedStats : FALLBACK_DATA;

  return (
    <AnalyticsWidgetCard.Root
      title={t("component.analytics-widget.community-feed-insights.title")}
      description={t("component.analytics-widget.community-feed-insights.description")}
      isLoading={isPending}
      isError={isError}
      error={error}
      isDataAvailable={isDataAvailable}
      href={routes.messageFeed.list({
        slug,
      })}
    >
      <AnalyticsWidgetCard.Body>
        <div className="flex w-full flex-col items-center gap-2">
          {/* Residents post count */}
          <section className="flex w-full flex-col gap-1 rounded-lg border border-grey-100 p-4">
            <span className="text-overline-bold text-grey-700">
              {t("component.analytics-widget.community-feed-insights.resident-posts.label")}
            </span>
            <p className="text-body-bold">
              {t("component.analytics-widget.community-feed-insights.resident-posts.content", {
                count: primaryData.totalResidentPostCount ?? 0,
              })}
            </p>
          </section>

          {/* Most reacted post */}
          <Link
            to={routes.messageFeed.details({
              slug,
              id: primaryData.mostReactedMessage?.id || "",
            })}
            className="flex w-full items-center justify-between rounded-lg border border-grey-100 p-4 transition-colors hover:bg-aop-basic-blue-100"
          >
            <div className="flex w-2/3 flex-col gap-1">
              <span className="text-overline-bold text-grey-700">
                {t("component.analytics-widget.community-feed-insights.most-reacted-post.label")}
              </span>
              <p className="truncate text-body-bold">{primaryData.mostReactedMessage?.title ?? "-"}</p>
            </div>
            {primaryData.mostReactedMessage && (
              <ReactionCountLabel reactions={primaryData.mostReactedMessage.totalReactionCountByType} />
            )}
          </Link>

          {/* Most commented post */}
          <Link
            to={routes.messageFeed.details({
              slug,
              id: primaryData.mostCommentedMessage?.id || "",
            })}
            className="flex w-full items-center justify-between rounded-lg border border-grey-100 p-4 transition-colors hover:bg-aop-basic-blue-100"
          >
            <div className="flex w-2/3 flex-1 flex-col gap-1">
              <span className="text-overline-bold text-grey-700">
                {t("component.analytics-widget.community-feed-insights.most-commented-post.label")}
              </span>
              <p className="truncate text-body-bold">{primaryData.mostCommentedMessage?.title ?? "-"}</p>
            </div>
            {primaryData.mostCommentedMessage && (
              <CommentCountLabel amountComments={primaryData.mostCommentedMessage.totalCommentCount} />
            )}
          </Link>
        </div>
      </AnalyticsWidgetCard.Body>
      <AnalyticsWidgetCard.Footer>
        <div className="flex w-full flex-col gap-2">
          <hr className="h-px w-full text-grey-100" />
          <div className="flex w-full items-center justify-end gap-2 text-grey-700">
            <Tooltip tooltip={t("component.analytics-widget.community-feed-insights.tooltip.last-updated")}>
              <time className="whitespace-nowrap">
                {t("common.label.last-updated", {
                  distance: formatDistance(t, {
                    start: communityFeedStats?.lastUpdatedAt ? new Date(communityFeedStats.lastUpdatedAt) : new Date(),
                    end: new Date(),
                  }),
                })}
              </time>
            </Tooltip>
          </div>
        </div>
      </AnalyticsWidgetCard.Footer>
    </AnalyticsWidgetCard.Root>
  );
}
type ReactionCountLabelProps = {
  reactions: MessageDetailV2Dto["totalReactionCountByType"];
};
const ReactionCountLabel = ({ reactions }: ReactionCountLabelProps) => {
  const { t } = useTranslation();
  const topReactions = Object.entries(reactions)
    .filter(([, count]) => count > 0)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map((entry) => entry[0]);

  return (
    <span className="flex h-fit min-h-10 items-center gap-2 rounded-md bg-grey-100 px-2">
      <div className="flex items-center gap-[2px]">
        {topReactions.map((reactionType) => (
          <img
            key={reactionType}
            src={getReactionImage(reactionType)}
            alt={getReactionLabel(reactionType, t)}
            className="h-5"
          />
        ))}
      </div>
      <span className="text-body text-black">{getTotalReactionAmount(reactions)}</span>
    </span>
  );
};
type CommentCountLLabelProps = {
  amountComments: number;
};
const CommentCountLabel = ({ amountComments }: CommentCountLLabelProps) => {
  return (
    <span className="flex h-fit min-h-10 items-center gap-2 rounded-md bg-grey-100 px-2 text-black">
      <MessageSquare02Icon size={20} />
      {amountComments}
    </span>
  );
};
